import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, LogOut, User, Calendar as CalendarIcon, MessageSquare, LayoutDashboard, Users, Settings, Clock, Plus, X, Trash2, Edit, Repeat, AlertTriangle, GripVertical, ChevronDown, Shield, Bell, Building2, Palette, Save, Eye, EyeOff, Image, Star, Menu } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { WelcomeBanner } from '../components/dashboard/WelcomeBanner';
import { QuickActions } from '../components/dashboard/QuickActions';
import { WeatherCard } from '../components/dashboard/WeatherCard';
import { EarningsCard } from '../components/dashboard/EarningsCard';
import { TodaysJobs } from '../components/dashboard/TodaysJobs';
import { useOkcWeather, rainChance, RAIN_ALERT_THRESHOLD } from '../components/dashboard/useOkcWeather';
import { GrassDivider } from '../components/SectionDecor';
import { customerKey, countUniqueCustomers, getTodaysJobs, getEarnings } from '../components/dashboard/dashboardStats';

const localizer = momentLocalizer(moment);
// Use relative API paths to work on any domain
// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for employee assignment
const EMPLOYEE_COLORS = [
  '#16a34a', // green
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#6366f1', // indigo
];

// Status badge colors
const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-lime-100 text-green-800 border-green-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300'
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const weather = useOkcWeather();
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  // Dialog states
  const [showNewEmployeeDialog, setShowNewEmployeeDialog] = useState(false);
  const [showNewAppointmentDialog, setShowNewAppointmentDialog] = useState(false);
  const [showEditEmployeeDialog, setShowEditEmployeeDialog] = useState(false);
  const [showEditAppointmentDialog, setShowEditAppointmentDialog] = useState(false);
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [showCustomerDetailsDialog, setShowCustomerDetailsDialog] = useState(false);
  const [showQuoteDetailsDialog, setShowQuoteDetailsDialog] = useState(false);
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [showRecurringCustomerDialog, setShowRecurringCustomerDialog] = useState(false);
  const [selectedRecurringCustomer, setSelectedRecurringCustomer] = useState(null);
  const [editingRecurringFrequency, setEditingRecurringFrequency] = useState('');
  const [editingRecurringPrice, setEditingRecurringPrice] = useState('');
  const [editingMowingOrder, setEditingMowingOrder] = useState(false);
  const [selectedScheduleSection, setSelectedScheduleSection] = useState('');
  const [scheduleCustomers, setScheduleCustomers] = useState({
    '1st': [],
    '2nd': [],
    '3rd': [],
    '4th': []
  });
  const [showCustomerBookings, setShowCustomerBookings] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [settingsTab, setSettingsTab] = useState('account');
  const [businessSettings, setBusinessSettings] = useState({
    business_name: 'Walker Lawn Care',
    business_phone: '(572) 205-7331',
    business_email: 'walkerlawncare21@gmail.com',
    business_address: 'Oklahoma City, OK',
    default_service_areas: ['Oklahoma City', 'Edmond', 'Midwest City', 'Del City', 'Choctaw', 'Forest Park', 'Spencer'],
    notifications: {
      email_on_new_quote: true,
      email_on_booking: true,
      sms_notifications: false
    },
    working_hours: {
      start: '08:00',
      end: '18:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    }
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [siteReviews, setSiteReviews] = useState([]);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoCaption, setNewPhotoCaption] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newReview, setNewReview] = useState({ name: '', location: '', rating: 5, text: '' });
  const [editingReview, setEditingReview] = useState(null);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [showAddRecurringCustomerDialog, setShowAddRecurringCustomerDialog] = useState(false);
  const [showMowingOrderDialog, setShowMowingOrderDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    location: ''
  });
  const [newRecurringCustomer, setNewRecurringCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    frequency: 'Weekly',
    price: ''
  });
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [editCustomer, setEditCustomer] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    location: ''
  });
  
  // State for progressive appointment loading
  const [weeksToShow, setWeeksToShow] = useState(1);
  
  // Search state for customers
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [recurringCustomerSearchQuery, setRecurringCustomerSearchQuery] = useState('');
  
  // Filter states
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    phone: '',
    password: '',
    role: 'employee'
  });

  const [editEmployee, setEditEmployee] = useState({
    name: '',
    phone: '',
    role: '',
    password: '',
    availability: {}
  });

  const [newAppointment, setNewAppointment] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    services: [],  // Changed from service_type to services array
    start_time: '',
    notes: '',
    assigned_employees: [],
    status: 'pending',
    is_recurring: false,
    recurrence_frequency: '' // e.g., "Weekly", "Bi-weekly", "Monthly", etc.
  });

  // Autofill customer details when name matches existing customer
  const handleCustomerNameChange = (name) => {
    if (name.length >= 2) {
      // Get unique customers from appointments
      const uniqueCustomers = new Map();
      appointments.forEach(apt => {
        if (apt.customer_name && apt.customer_email) {
          uniqueCustomers.set(apt.customer_email, {
            name: apt.customer_name,
            email: apt.customer_email,
            phone: apt.customer_phone,
            address: apt.customer_address
          });
        }
      });
      
      // Filter customers by name (case-insensitive partial match)
      const filtered = Array.from(uniqueCustomers.values()).filter(customer =>
        customer.name.toLowerCase().includes(name.toLowerCase())
      );
      
      if (filtered.length > 0) {
        setCustomerSuggestions(filtered);
        setShowCustomerSuggestions(true);
      } else {
        setShowCustomerSuggestions(false);
      }
    } else {
      setShowCustomerSuggestions(false);
    }
    
    // Update the name field
    setNewAppointment({ ...newAppointment, customer_name: name });
  };

  const handleSelectCustomer = (customer) => {
    setNewAppointment({
      ...newAppointment,
      customer_name: customer.name,
      customer_phone: customer.phone || '',
      customer_email: customer.email || '',
      customer_address: customer.address || ''
    });
    setShowCustomerSuggestions(false);
  };

  const [editAppointment, setEditAppointment] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    services: [],  // Changed from service_type to services array
    start_time: '',
    notes: '',
    assigned_employees: [],
    status: 'pending'
  });

  const [recurringSettings, setRecurringSettings] = useState({
    frequency: 'weekly',
    interval: 1,
    end_date: '',
    occurrences: 10
  });

  useEffect(() => {
    // Small delay to ensure cookies from login are fully processed
    const timer = setTimeout(async () => {
      if (!(await checkAuth())) return;
      fetchSubmissions();
      fetchEmployees();
      fetchAppointments();
      fetchSettings();
      fetchGallery();
      fetchSiteReviews();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      return true;
    } catch (error) {
      toast.error('Please login first');
      navigate('/');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`/api/submissions`, {
        withCredentials: true
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`/api/employees`, {
        withCredentials: true
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`/api/appointments`, {
        withCredentials: true
      });
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`/api/settings`, { withCredentials: true });
      setBusinessSettings(response.data);
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`/api/settings`, businessSettings, { withCredentials: true });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`/api/gallery`, { withCredentials: true });
      setGalleryPhotos(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const fetchSiteReviews = async () => {
    try {
      const response = await axios.get(`/api/reviews`, { withCredentials: true });
      setSiteReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleAddPhoto = async () => {
    if (!newPhotoUrl.trim()) { toast.error('Please enter a photo URL'); return; }
    try {
      await axios.post(`/api/gallery`, { url: newPhotoUrl, caption: newPhotoCaption }, { withCredentials: true });
      toast.success('Photo added');
      setNewPhotoUrl('');
      setNewPhotoCaption('');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to add photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`/api/gallery/${photoId}`, { withCredentials: true });
      toast.success('Photo deleted');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`/api/gallery/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded!');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleReorderPhoto = async (photoId, direction) => {
    const idx = galleryPhotos.findIndex(p => p.id === photoId);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= galleryPhotos.length) return;
    const reordered = [...galleryPhotos];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setGalleryPhotos(reordered);
    try {
      await axios.put(`/api/gallery/reorder`, { order: reordered.map(p => p.id) }, { withCredentials: true });
    } catch (error) {
      toast.error('Failed to save order');
      fetchGallery();
    }
  };

  const handleAddReview = async () => {
    if (!newReview.name || !newReview.text) { toast.error('Name and review text are required'); return; }
    try {
      await axios.post(`/api/reviews`, newReview, { withCredentials: true });
      toast.success('Review added');
      setNewReview({ name: '', location: '', rating: 5, text: '' });
      fetchSiteReviews();
    } catch (error) {
      toast.error('Failed to add review');
    }
  };

  const handleUpdateReview = async () => {
    if (!editingReview) return;
    try {
      await axios.put(`/api/reviews/${editingReview.id}`, editingReview, { withCredentials: true });
      toast.success('Review updated');
      setEditingReview(null);
      fetchSiteReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`/api/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted');
      fetchSiteReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  // Helper function to get color for employee
  const getEmployeeColor = (employeeId) => {
    if (!employeeId) return EMPLOYEE_COLORS[0];
    const index = employees.findIndex(emp => emp.id === employeeId);
    return EMPLOYEE_COLORS[index % EMPLOYEE_COLORS.length] || EMPLOYEE_COLORS[0];
  };

  // Helper to get employee name
  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? emp.name : 'Unknown';
  };

  // Check if employee is available at given time
  const checkEmployeeAvailability = (employeeId, startTime, endTime) => {
    const emp = employees.find(e => e.id === employeeId);
    if (!emp || !emp.availability || Object.keys(emp.availability).length === 0) {
      return { available: true, message: 'No availability set' };
    }

    const start = new Date(startTime);
    const dayOfWeek = start.toLocaleDateString('en-US', { weekday: 'lowercase' });
    const dayAvailability = emp.availability[dayOfWeek];

    if (!dayAvailability || dayAvailability.length === 0) {
      return { available: false, message: `${emp.name} not available on ${dayOfWeek}s` };
    }

    const startHour = start.getHours() + start.getMinutes() / 60;
    const end = new Date(endTime);
    const endHour = end.getHours() + end.getMinutes() / 60;

    for (const slot of dayAvailability) {
      const [slotStart, slotEnd] = slot.split('-').map(t => {
        const [h, m] = t.trim().split(':');
        return parseInt(h) + parseInt(m) / 60;
      });

      if (startHour >= slotStart && endHour <= slotEnd) {
        return { available: true, message: 'Available' };
      }
    }

    return { available: false, message: `${emp.name} not available at this time` };
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`/api/employees`, newEmployee, {
        withCredentials: true
      });
      
      toast.success(`Employee created! Username: ${response.data.username}`);
      setShowNewEmployeeDialog(false);
      setNewEmployee({ name: '', phone: '', password: '', role: 'employee' });
      fetchEmployees();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEditEmployee({
      name: employee.name,
      phone: employee.phone || '',
      role: employee.role,
      password: '',
      availability: employee.availability || {}
    });
    setShowEditEmployeeDialog(true);
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        name: editEmployee.name,
        phone: editEmployee.phone,
        role: editEmployee.role,
        availability: editEmployee.availability
      };
      
      // Only include password if it was changed
      if (editEmployee.password) {
        updateData.password = editEmployee.password;
      }
      
      await axios.put(`/api/employees/${editingEmployee.id}`, updateData, {
        withCredentials: true
      });
      
      toast.success('Employee updated successfully!');
      setShowEditEmployeeDialog(false);
      setEditingEmployee(null);
      setEditEmployee({ name: '', phone: '', role: '', password: '', availability: {} });
      fetchEmployees();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const handleDeleteEmployee = async (employee) => {
    if (!window.confirm(`Are you sure you want to remove ${employee.name} from the team?`)) {
      return;
    }
    
    try {
      await axios.delete(`/api/employees/${employee.username}`, {
        withCredentials: true
      });
      
      toast.success('Employee removed successfully!');
      fetchEmployees();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove employee');
    }
  };

  const handleEditCustomer = (customer) => {
    setEditCustomer({
      id: customer.id,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      location: customer.location || ''
    });
    setShowEditCustomerDialog(true);
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    
    try {
      // Find the appointment for this customer by name
      const customerApt = appointments.find(
        apt => apt.customer_name && apt.customer_name.toLowerCase() === editCustomer.name.toLowerCase()
      );
      
      if (!customerApt) {
        toast.error('Customer not found in appointments');
        return;
      }
      
      await axios.put(`/api/appointments/${customerApt.id}`, {
        customer_name: editCustomer.name,
        customer_email: editCustomer.email,
        customer_phone: editCustomer.phone,
        customer_address: editCustomer.location
      }, {
        withCredentials: true
      });
      
      toast.success('Customer information updated!');
      setShowEditCustomerDialog(false);
      setShowCustomerDetailsDialog(false);
      fetchAppointments();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newCustomer.name || !newCustomer.location) {
      toast.error('Name and location are required');
      return;
    }
    
    try {
      // Check if customer with same name already exists
      const existingCustomer = appointments.find(
        apt => apt.customer_name && apt.customer_name.toLowerCase() === newCustomer.name.toLowerCase()
      );
      
      if (existingCustomer) {
        // Update existing customer instead of creating new
        await axios.put(`/api/appointments/${existingCustomer.id}`, {
          customer_phone: newCustomer.phone || existingCustomer.customer_phone,
          customer_email: newCustomer.email || existingCustomer.customer_email,
          customer_address: newCustomer.location || existingCustomer.customer_address,
        }, {
          withCredentials: true
        });
        
        toast.success('Linked to existing customer and updated info!');
      } else {
        // Create new customer as an appointment entry
        const now = new Date();
        const appointmentData = {
          customer_name: newCustomer.name,
          customer_phone: newCustomer.phone || '',
          customer_email: newCustomer.email || '',
          customer_address: newCustomer.location,
          services: [],
          start_time: now.toISOString(),
          end_time: now.toISOString(),
          notes: 'Customer profile (no appointment)',
          assigned_employees: [],
          status: 'customer_only',
          is_recurring: false
        };
        
        await axios.post(`/api/appointments`, appointmentData, {
          withCredentials: true
        });
        
        toast.success('Customer added successfully!');
      }
      
      setShowAddCustomerDialog(false);
      setNewCustomer({ name: '', email: '', phone: '', location: '' });
      fetchAppointments();
      
    } catch (error) {
      console.error('Add customer error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to add customer';
      toast.error(errorMsg);
    }
  };

  const handleAddRecurringCustomer = async (e) => {
    e.preventDefault();
    
    try {
      // Check if customer with same name already exists
      const existingCustomer = appointments.find(
        apt => apt.customer_name.toLowerCase() === newRecurringCustomer.name.toLowerCase()
      );
      
      if (existingCustomer) {
        // Update existing customer to be recurring
        await axios.put(`/api/appointments/${existingCustomer.id}`, {
          customer_phone: newRecurringCustomer.phone || existingCustomer.customer_phone,
          customer_email: newRecurringCustomer.email || existingCustomer.customer_email,
          customer_address: newRecurringCustomer.location || existingCustomer.customer_address,
          is_recurring: true,
          recurrence_frequency: newRecurringCustomer.frequency,
          price: newRecurringCustomer.price,
          notes: selectedScheduleSection ? `Recurring - ${newRecurringCustomer.frequency} - ${selectedScheduleSection}` : existingCustomer.notes
        }, {
          withCredentials: true
        });
        
        toast.success(`Linked to existing customer and made recurring in ${selectedScheduleSection || 'schedule'}!`);
      } else {
        // Create new recurring customer
        const appointmentData = {
          customer_name: newRecurringCustomer.name,
          customer_phone: newRecurringCustomer.phone || null,
          customer_email: newRecurringCustomer.email || null,
          customer_address: newRecurringCustomer.location,
          services: [],
          start_time: new Date().toISOString().slice(0, 16),
          end_time: new Date().toISOString().slice(0, 16),
          notes: selectedScheduleSection ? `Recurring - ${newRecurringCustomer.frequency} - ${selectedScheduleSection}` : `Recurring customer - ${newRecurringCustomer.frequency}`,
          assigned_employees: [],
          status: 'customer_only',
          is_recurring: true,
          recurrence_frequency: newRecurringCustomer.frequency,
          price: newRecurringCustomer.price
        };
        
        await axios.post(`/api/appointments`, appointmentData, {
          withCredentials: true
        });
        
        toast.success(`Recurring customer added to ${selectedScheduleSection || 'schedule'}!`);
      }
      
      setShowAddRecurringCustomerDialog(false);
      setNewRecurringCustomer({ name: '', email: '', phone: '', location: '', frequency: 'Weekly', price: '' });
      setSelectedScheduleSection('');
      fetchAppointments();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add recurring customer');
    }
  };

  // Helper function to view customer from mowing order
  const viewCustomerFromMowingOrder = (customerName) => {
    // Try exact match first, then extract name before " - "
    let customer = appointments.find(apt => 
      apt.customer_name && apt.customer_name.toLowerCase() === customerName.toLowerCase()
    );
    
    if (!customer) {
      const cleanName = customerName.split(' - ')[0].trim();
      customer = appointments.find(apt => 
        apt.customer_name && apt.customer_name.toLowerCase() === cleanName.toLowerCase()
      );
    }
    
    if (customer) {
      setSelectedCustomer({
        name: customer.customer_name,
        email: customer.customer_email || '',
        phone: customer.customer_phone || '',
        location: customer.customer_address || '',
        id: customer.id,
        is_recurring: customer.is_recurring || false,
        recurrence_frequency: customer.recurrence_frequency || '',
        appointments: [customer],
        quotes: []
      });
      setShowCustomerDetailsDialog(true);
      setShowMowingOrderDialog(false);
    } else {
      toast.error(`Customer "${customerName}" not found. Make sure to add them in the Customers section first.`);
    }
  };

  const handleChangeFrequencyFromDetails = async (newFrequency) => {
    if (!selectedCustomer) return;
    
    try {
      // Find the appointment for this customer
      const customerApt = appointments.find(
        apt => apt.customer_name && apt.customer_name.toLowerCase() === selectedCustomer.name.toLowerCase()
      );
      
      if (!customerApt) {
        toast.error('Customer appointment not found');
        return;
      }
      
      await axios.put(`/api/appointments/${customerApt.id}`, {
        recurrence_frequency: newFrequency
      }, {
        withCredentials: true
      });
      
      // Update the selectedCustomer state immediately
      setSelectedCustomer(prev => ({ ...prev, recurrence_frequency: newFrequency }));
      toast.success(`Frequency updated to ${newFrequency}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update frequency');
    }
  };

  // Load schedule customers from appointments
  useEffect(() => {
    const schedules = {
      '1st': [],
      '2nd': [],
      '3rd': [],
      '4th': []
    };

    appointments.forEach(apt => {
      if (apt.is_recurring && apt.notes) {
        if (apt.notes.includes('1st Schedule')) schedules['1st'].push(apt);
        else if (apt.notes.includes('2nd Schedule')) schedules['2nd'].push(apt);
        else if (apt.notes.includes('3rd Schedule')) schedules['3rd'].push(apt);
        else if (apt.notes.includes('4th Schedule')) schedules['4th'].push(apt);
      }
    });

    setScheduleCustomers(schedules);
  }, [appointments]);

  // Move customer up in schedule
  const moveCustomerUp = async (scheduleKey, index) => {
    if (index === 0) return;
    
    const schedule = [...scheduleCustomers[scheduleKey]];
    [schedule[index], schedule[index - 1]] = [schedule[index - 1], schedule[index]];
    
    setScheduleCustomers({
      ...scheduleCustomers,
      [scheduleKey]: schedule
    });
    
    toast.success('Customer moved up');
  };

  // Move customer down in schedule
  const moveCustomerDown = async (scheduleKey, index) => {
    const schedule = [...scheduleCustomers[scheduleKey]];
    if (index === schedule.length - 1) return;
    
    [schedule[index], schedule[index + 1]] = [schedule[index + 1], schedule[index]];
    
    setScheduleCustomers({
      ...scheduleCustomers,
      [scheduleKey]: schedule
    });
    
    toast.success('Customer moved down');
  };

  // Move customer to different schedule
  const moveCustomerToSchedule = async (customer, fromSchedule, toSchedule) => {
    try {
      const scheduleNames = {
        '1st': '1st Schedule - Friday',
        '2nd': '2nd Schedule - Thursday',
        '3rd': '3rd Schedule - Friday',
        '4th': '4th Schedule - Thursday'
      };

      await axios.put(`/api/appointments/${customer.id}`, {
        notes: `Recurring - ${customer.recurrence_frequency} - ${scheduleNames[toSchedule]}`
      }, {
        withCredentials: true
      });

      toast.success(`Moved to ${scheduleNames[toSchedule]}!`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to move customer');
    }
  };

  // Remove from recurring (set is_recurring to false)
  const removeFromRecurring = async (customerId) => {
    if (!window.confirm('Remove this customer from recurring schedule? They will stay in regular customers.')) {
      return;
    }

    try {
      await axios.put(`/api/appointments/${customerId}`, {
        is_recurring: false,
        recurrence_frequency: null,
        price: null
      }, {
        withCredentials: true
      });

      toast.success('Removed from recurring customers');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to remove customer');
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    
    // Auto-calculate end_time as start_time + 1 hour
    const startDate = new Date(newAppointment.start_time);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
    const endTime = formatDateTimeForInput(endDate);
    
    const appointmentData = {
      ...newAppointment,
      end_time: endTime
    };
    
    // Check availability for assigned employees
    if (appointmentData.assigned_employees.length > 0) {
      const warnings = [];
      for (const empId of appointmentData.assigned_employees) {
        const availCheck = checkEmployeeAvailability(empId, appointmentData.start_time, appointmentData.end_time);
        if (!availCheck.available) {
          warnings.push(availCheck.message);
        }
      }
      
      if (warnings.length > 0) {
        const proceed = window.confirm(`⚠️ Availability Warning:\n\n${warnings.join('\n')}\n\nDo you want to proceed anyway?`);
        if (!proceed) return;
      }
    }
    
    try {
      // Create appointment
      await axios.post(`/api/appointments`, appointmentData, {
        withCredentials: true
      });
      
      // Auto-create customer submission if they don't exist
      const existingCustomer = submissions.find(s => 
        s.email === newAppointment.customer_email || s.phone === newAppointment.customer_phone
      );
      
      if (!existingCustomer && newAppointment.customer_email) {
        // Format appointment details for customer record
        const appointmentDate = new Date(newAppointment.start_time);
        const formattedDate = appointmentDate.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        });
        
        const appointmentMessage = `Appointment scheduled for ${formattedDate}${newAppointment.notes ? `. Notes: ${newAppointment.notes}` : ''}`;
        
        // Create a submission entry for this customer
        await axios.post(`/api/contact`, {
          name: newAppointment.customer_name,
          email: newAppointment.customer_email,
          phone: newAppointment.customer_phone,
          location: newAppointment.customer_address,
          services: newAppointment.services,
          message: appointmentMessage
        }, {
          withCredentials: true
        });
      }
      
      toast.success('Appointment created successfully!');
      setShowNewAppointmentDialog(false);
      setNewAppointment({
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        customer_address: '',
        services: [],
        start_time: '',
        notes: '',
        assigned_employees: [],
        status: 'pending',
        is_recurring: false,
        recurrence_frequency: ''
      });
      fetchAppointments();
      fetchSubmissions(); // Refresh to update customer list
      
    } catch (error) {
      toast.error('Failed to create appointment');
    }
  };

  // Helper to format datetime for datetime-local input (yyyy-MM-ddThh:mm)
  const formatDateTimeForInput = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    // Handle ISO format with microseconds or any other format
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) return '';
    // Format as yyyy-MM-ddThh:mm
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setEditAppointment({
      title: appointment.title || appointment.customer_name || "",
      customer_name: appointment.customer_name,
      customer_phone: appointment.customer_phone,
      customer_email: appointment.customer_email || '',
      customer_address: appointment.customer_address || '',
      services: appointment.services || (appointment.service_type ? [appointment.service_type] : []),  // Handle backward compatibility
      start_time: formatDateTimeForInput(appointment.start_time),
      notes: appointment.notes || '',
      assigned_employees: appointment.assigned_employees || [],
      status: appointment.status || 'pending'
    });
    
    // Check if recurring and ask what to edit
    if (appointment.is_recurring) {
      const choice = window.confirm('This is a recurring appointment.\n\nClick OK to edit ONLY this occurrence\nClick Cancel to edit ALL future occurrences');
      // For now, we'll just edit this single occurrence
      // In a full implementation, this would set a flag to handle series editing
    }
    
    setShowEditAppointmentDialog(true);
  };

  const handleUpdateAppointment = async (e) => {
    e.preventDefault();
    
    // Auto-calculate end_time as start_time + 1 hour
    const startDate = new Date(editAppointment.start_time);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
    const endTime = formatDateTimeForInput(endDate);
    
    const appointmentData = {
      ...editAppointment,
      end_time: endTime
    };
    
    // Check availability for assigned employees
    if (appointmentData.assigned_employees.length > 0 && appointmentData.start_time && appointmentData.end_time) {
      const warnings = [];
      for (const empId of appointmentData.assigned_employees) {
        const availCheck = checkEmployeeAvailability(empId, appointmentData.start_time, appointmentData.end_time);
        if (!availCheck.available) {
          warnings.push(availCheck.message);
        }
      }
      
      if (warnings.length > 0) {
        const proceed = window.confirm(`⚠️ Availability Warning:\n\n${warnings.join('\n')}\n\nDo you want to proceed anyway?`);
        if (!proceed) return;
      }
    }
    
    try {
      await axios.put(`/api/appointments/${editingAppointment.id}`, appointmentData, {
        withCredentials: true
      });
      
      toast.success('Appointment updated successfully!');
      setShowEditAppointmentDialog(false);
      setEditingAppointment(null);
      setEditAppointment({
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        customer_address: '',
        services: [],
        start_time: '',
        notes: '',
        assigned_employees: [],
        status: 'pending'
      });
      fetchAppointments();
      
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await axios.delete(`/api/appointments/${appointmentId}`, {
        withCredentials: true
      });
      toast.success('Appointment deleted');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer? This will remove ALL their appointments and quote requests from the database.')) {
      return;
    }
    
    try {
      const customer = selectedCustomer;
      
      // Delete all appointments for this customer
      if (customer.appointments && customer.appointments.length > 0) {
        for (const appointment of customer.appointments) {
          await axios.delete(`/api/appointments/${appointment.id}`, {
            withCredentials: true
          });
        }
      }
      
      // Delete all quote submissions for this customer
      if (customer.quotes && customer.quotes.length > 0) {
        for (const quote of customer.quotes) {
          await axios.delete(`/api/submissions/${quote.id}`, {
            withCredentials: true
          });
        }
      }
      
      toast.success('Customer and all their data deleted successfully');
      setShowCustomerDetailsDialog(false);
      setShowEditCustomerDialog(false);
      fetchSubmissions();
      fetchAppointments(); // Refresh appointments to update schedule
    } catch (error) {
      toast.error('Failed to delete customer: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote request?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/submissions/${quoteId}`, {
        withCredentials: true
      });
      toast.success('Quote request deleted');
      setShowQuoteDetailsDialog(false);
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to delete quote request');
    }
  };

  const handleToggleRecurringStatus = async (customer, currentStatus) => {
    try {
      // Find the recurring appointment for this customer by name (since email is optional)
      const recurringApt = appointments.find(
        apt => apt.customer_name && apt.customer_name.toLowerCase() === customer.name.toLowerCase() && apt.is_recurring
      );
      
      if (!recurringApt) {
        toast.error('Recurring appointment not found for this customer');
        return;
      }
      
      // Toggle the active status by updating a custom field
      const newStatus = !currentStatus;
      await axios.put(`/api/appointments/${recurringApt.id}`, {
        recurring_active: newStatus
      }, {
        withCredentials: true
      });
      
      toast.success(newStatus ? 'Recurring appointment activated' : 'Recurring appointment paused');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update recurring status');
    }
  };

  const handleChangeRecurringFrequency = async () => {
    if (!editingRecurringFrequency) {
      toast.error('Please select a frequency');
      return;
    }
    
    try {
      // Find the recurring appointment for this customer
      const recurringApt = appointments.find(
        apt => apt.is_recurring && apt.customer_name && selectedRecurringCustomer.name &&
          apt.customer_name.toLowerCase() === selectedRecurringCustomer.name.toLowerCase()
      );
      
      if (!recurringApt) {
        toast.error('Recurring appointment not found');
        return;
      }
      
      await axios.put(`/api/appointments/${recurringApt.id}`, {
        recurrence_frequency: editingRecurringFrequency,
        price: editingRecurringPrice
      }, {
        withCredentials: true
      });
      
      toast.success('Frequency and price updated successfully');
      setShowRecurringCustomerDialog(false);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update frequency');
    }
  };

  const handleCancelRecurring = async () => {
    if (!window.confirm('Are you sure you want to cancel this recurring appointment? This will remove the customer from recurring customers.')) {
      return;
    }
    
    try {
      // Find the recurring appointment for this customer
      const recurringApt = appointments.find(
        apt => apt.is_recurring && apt.customer_name && selectedRecurringCustomer.name &&
          apt.customer_name.toLowerCase() === selectedRecurringCustomer.name.toLowerCase()
      );
      
      if (!recurringApt) {
        toast.error('Recurring appointment not found');
        return;
      }
      
      // Set is_recurring to false to cancel the recurring status
      await axios.put(`/api/appointments/${recurringApt.id}`, {
        is_recurring: false,
        recurrence_frequency: null
      }, {
        withCredentials: true
      });
      
      toast.success('Recurring appointment cancelled');
      setShowRecurringCustomerDialog(false);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to cancel recurring appointment');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post('/api/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        withCredentials: true
      });
      
      toast.success('Password changed successfully');
      setShowChangePasswordDialog(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleBookAppointmentForCustomer = (customerData) => {
    // Pre-fill appointment form with customer data
    setNewAppointment({
      customer_name: customerData.name || '',
      customer_phone: customerData.phone || '',
      customer_email: customerData.email || '',
      customer_address: customerData.location || customerData.address || '',
      services: [],
      start_time: '',
      notes: '',
      assigned_employees: [],
      status: 'pending',
      is_recurring: false,
      recurrence_frequency: ''
    });
    setShowNewAppointmentDialog(true);
  };


  const handleLogout = async () => {
    try {
      await axios.post(`/api/auth/logout`, {}, {
        withCredentials: true
      });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'quotes', label: 'Quote Requests', icon: MessageSquare },
    { id: 'schedule', label: 'Schedule', icon: CalendarIcon },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'employees', label: 'Employees', icon: User },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Filter appointments
  const filteredAppointments = appointments.filter(apt => {
    // Exclude customer-only entries (they're not real appointments)
    if (apt.status === 'customer_only') {
      return false;
    }
    
    // Filter by employee
    if (selectedEmployeeFilter !== 'all') {
      if (!apt.assigned_employees || !apt.assigned_employees.includes(selectedEmployeeFilter)) {
        return false;
      }
    }
    
    // Filter by status
    if (selectedStatusFilter !== 'all') {
      if (apt.status !== selectedStatusFilter) {
        return false;
      }
    }
    
    return true;
  });

  // Convert appointments for calendar with color coding
  const calendarEvents = filteredAppointments.map(apt => {
    const assignedEmployees = apt.assigned_employees || [];
    const primaryEmployee = assignedEmployees[0];
    const color = primaryEmployee ? getEmployeeColor(primaryEmployee) : '#16a34a';
    
    return {
      id: apt.id,  // Use custom id field
      title: apt.customer_name, // Display customer name on calendar
      start: new Date(apt.start_time),
      end: new Date(apt.end_time),
      resource: apt,
      style: {
        backgroundColor: color,
        borderColor: color
      }
    };
  });

  // Custom event style getter for calendar
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.style?.backgroundColor || '#16a34a',
        borderColor: event.style?.borderColor || '#16a34a',
        color: 'white',
        borderRadius: '5px',
        border: 'none',
        display: 'block'
      }
    };
  };

  const renderSchedule = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Schedule & Appointments</h2>
        <div className="flex flex-wrap gap-3">
          <Button 
            variant="outline"
            className="border-lime-600 text-lime-700 hover:bg-lime-50"
            onClick={() => setShowMowingOrderDialog(true)}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            View Mowing Order
          </Button>
          <Button 
            className="bg-green-700 hover:bg-green-800"
            onClick={() => setShowNewAppointmentDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-stone-50">
        <CardContent className="p-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium">Filter by Employee</Label>
              <select
                className="w-full border rounded p-2 mt-1"
                value={selectedEmployeeFilter}
                onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
              >
                <option value="all">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium">Filter by Status</Label>
              <select
                className="w-full border rounded p-2 mt-1"
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-stone-600 bg-white px-3 py-2 rounded border">
                <span className="font-semibold">{filteredAppointments.length}</span> of <span className="font-semibold">{appointments.length}</span> appointments
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-2 md:p-6">
          {/* Mobile-friendly Add Appointment Button */}
          <div className="md:hidden mb-4">
            <Button 
              className="w-full bg-green-700 hover:bg-green-800"
              onClick={() => {
                // Set current date/time as default
                const now = new Date();
                now.setMinutes(0);
                const formatForInput = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                };
                setNewAppointment({
                  ...newAppointment,
                  start_time: formatForInput(now)
                });
                setShowNewAppointmentDialog(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Tap to Add Appointment
            </Button>
          </div>
          
          {/* Responsive calendar container - smaller on mobile, proper width */}
          <div className="h-[400px] md:h-[600px] w-full">
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%', width: '100%' }}
              eventPropGetter={eventStyleGetter}
              selectable
              longPressThreshold={10}
              views={['month']}
              date={calendarDate}
              onNavigate={setCalendarDate}
              onSelectSlot={(slotInfo) => {
                // Format date/time for datetime-local input
                const start = new Date(slotInfo.start);
                
                // If clicking on a day in month view, set time to 9 AM
                if (slotInfo.action === 'click') {
                  start.setHours(9, 0, 0, 0);
                }
                
                const formatForInput = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                };
                
                // Pre-fill appointment with selected date/time (end_time auto-calculated on submit)
                setNewAppointment({
                  ...newAppointment,
                  start_time: formatForInput(start)
                });
                
                // Open dialog
                setShowNewAppointmentDialog(true);
              }}
              onSelectEvent={(event) => {
                handleEditAppointment(event.resource);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Upcoming Appointments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {appointments
              .filter(apt => apt.status !== 'customer_only')
              .slice(0, 5)
              .map((apt) => (
              <div key={apt.id} className="flex items-center justify-between p-4 border-2 border-stone-200 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold">{apt.customer_name}</p>
                    <span className={`text-xs px-2 py-1 rounded border ${STATUS_COLORS[apt.status] || STATUS_COLORS.pending}`}>
                      {apt.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                    </span>
                    {apt.is_recurring && (
                      <Repeat className="h-4 w-4 text-stone-600" title="Recurring" />
                    )}
                  </div>
                  <p className="text-sm text-stone-600">{apt.customer_name} - {apt.customer_phone}</p>
                  <p className="text-sm text-stone-500">{new Date(apt.start_time).toLocaleDateString()}</p>
                  {apt.assigned_employees && apt.assigned_employees.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <User className="h-3 w-3 text-green-700" />
                      <p className="text-xs text-green-700">
                        {apt.assigned_employees.map(empId => getEmployeeName(empId)).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-lime-700 border-lime-600 hover:bg-lime-50"
                    onClick={() => handleEditAppointment(apt)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-500 border-red-500 hover:bg-red-50"
                    onClick={() => handleDeleteAppointment(apt.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderEmployees = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Employee Management</h2>
        {user?.role === 'admin' && (
          <Dialog open={showNewEmployeeDialog} onOpenChange={setShowNewEmployeeDialog}>
            <DialogTrigger asChild>
              <Button className="bg-green-700 hover:bg-green-800">
                <Plus className="h-4 w-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Employee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateEmployee} className="space-y-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    required
                    data-testid="employee-name-input"
                    value={newEmployee.name}
                    onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                    placeholder="John Doe"
                  />
                  <p className="text-xs text-stone-500 mt-1">Username will be auto-generated from name</p>
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    required
                    type="tel"
                    data-testid="employee-phone-input"
                    value={newEmployee.phone}
                    onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    required
                    type="password"
                    data-testid="employee-password-input"
                    value={newEmployee.password}
                    onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                    placeholder="Strong password"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <select
                    className="w-full border rounded p-2"
                    data-testid="employee-role-select"
                    value={newEmployee.role}
                    onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})}
                  >
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
                <Button type="submit" data-testid="create-employee-btn" className="w-full bg-green-700 hover:bg-green-800">
                  Create Employee
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Employees ({employees.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {employees.map((emp) => (
              <div key={emp.id} className="flex items-center justify-between p-4 border-2 border-stone-200 rounded-lg hover:border-green-500 transition-all">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-lime-100 flex items-center justify-center">
                    <User className="h-6 w-6 text-green-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-900 break-words">{emp.name}</p>
                    <p className="text-sm text-stone-600 break-all truncate">@{emp.username}</p>
                    {emp.phone && (
                      <p className="text-sm text-stone-600 flex items-center gap-1 break-words">
                        <Phone className="h-3 w-3 flex-shrink-0" />
                        <span className="break-all">{emp.phone}</span>
                      </p>
                    )}
                    <p className="text-xs text-stone-500 capitalize">{emp.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-stone-600">Member since</p>
                    <p className="text-xs text-stone-500">{new Date(emp.created_at).toLocaleDateString()}</p>
                  </div>
                  {user?.role === 'admin' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-lime-700 border-lime-600 hover:bg-lime-50"
                        onClick={() => handleEditEmployee(emp)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 border-red-500 hover:bg-red-50"
                        onClick={() => handleDeleteEmployee(emp)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderDashboard = () => {
    // Calculate Quotes Today - submissions received today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const quotesToday = submissions.filter(s => {
      const submittedDate = new Date(s.submitted_at);
      return submittedDate >= today && submittedDate < tomorrow;
    }).length;
    
    // Calculate Appointments Today - appointments scheduled for today (exclude customer-only)
    const appointmentsToday = appointments.filter(apt => {
      if (apt.status === 'customer_only') return false;
      const aptDate = new Date(apt.start_time);
      aptDate.setHours(0, 0, 0, 0);
      return aptDate.getTime() === today.getTime();
    }).length;
    
    const todaysJobs = getTodaysJobs(appointments);
    const emailsWithAppointments = new Set(appointments.map(apt => apt.customer_email).filter(Boolean));
    const openQuotes = submissions.filter(sub => !emailsWithAppointments.has(sub.email)).length;

    return (
      <>
        <WelcomeBanner name={user?.name || user?.username} jobsToday={todaysJobs.length} newQuotes={quotesToday} />
        <QuickActions
          onNewAppointment={() => setShowNewAppointmentDialog(true)}
          onAddCustomer={() => setShowAddCustomerDialog(true)}
          onQuotes={() => setActiveSection('quotes')}
          onSchedule={() => setActiveSection('schedule')}
          quoteCount={openQuotes}
        />
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-5 mb-6">
          <Card className="border-0 shadow-sm bg-white rounded-2xl overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-500 mb-1 font-medium">Quotes Today</p>
                  <p className="text-3xl font-bold text-stone-900">{quotesToday}</p>
                  <p className="text-xs text-stone-400 mt-1">New requests</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-lime-50 flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-white rounded-2xl overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-500 mb-1 font-medium">Appointments</p>
                  <p className="text-3xl font-bold text-stone-900">{appointmentsToday}</p>
                  <p className="text-xs text-stone-400 mt-1">Scheduled today</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-lime-50 flex items-center justify-center">
                  <CalendarIcon className="h-6 w-6 text-lime-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-white rounded-2xl overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-500 mb-1 font-medium">Customers</p>
                  <p className="text-3xl font-bold text-stone-900">{countUniqueCustomers(appointments)}</p>
                  <p className="text-xs text-stone-400 mt-1">Total customers</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-lime-50 flex items-center justify-center">
                  <User className="h-6 w-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-white rounded-2xl overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-stone-500 mb-1 font-medium">Employees</p>
                  <p className="text-3xl font-bold text-stone-900">{employees.length}</p>
                  <p className="text-xs text-stone-400 mt-1">Team members</p>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-lime-50 flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          <div className="lg:col-span-2 min-w-0">
            <TodaysJobs jobs={todaysJobs} employees={employees} onOpen={handleEditAppointment} rain={rainChance(weather.data)} rainLikely={rainChance(weather.data) >= RAIN_ALERT_THRESHOLD} />
          </div>
          <div className="space-y-5 min-w-0">
            <WeatherCard data={weather.data} failed={weather.failed} />
            <EarningsCard earnings={getEarnings(appointments)} />
          </div>
        </div>

      {/* Recent Activity */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="border-b border-stone-100 pb-4">
          <CardTitle className="text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Recent Quote Requests</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-2">
            {(() => {
              // Filter out customers who already have appointments
              const customerEmailsWithAppointments = new Set(
                appointments.map(apt => apt.customer_email).filter(Boolean)
              );
              const recentQuoteOnlySubmissions = submissions
                .filter(sub => !customerEmailsWithAppointments.has(sub.email))
                .slice(0, 5);
              
              return recentQuoteOnlySubmissions.map((submission) => (
                <div 
                  key={submission.id} 
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                >
                  <div 
                    className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer"
                    onClick={() => {
                      setSelectedQuote(submission);
                      setShowQuoteDetailsDialog(true);
                    }}
                  >
                    <div className="h-9 w-9 shrink-0 rounded-full bg-lime-100 flex items-center justify-center">
                      <span className="text-green-700 font-bold text-xs">{(submission.name || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-stone-800 text-sm truncate">{submission.name}</p>
                      <p className="text-xs text-stone-500 truncate">{submission.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="hidden sm:block text-xs text-stone-400">{new Date(submission.submitted_at).toLocaleDateString()}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookAppointmentForCustomer(submission);
                      }}
                      className="border-green-600 text-green-600 hover:bg-lime-50 rounded-lg text-xs"
                    >
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      Book
                    </Button>
                  </div>
                </div>
              ));
            })()}
          </div>
        </CardContent>
      </Card>
    </>
    );
  };
  
  const renderQuotes = () => {
    // Filter out submissions from customers who already have appointments
    const customerEmailsWithAppointments = new Set(
      appointments.map(apt => apt.customer_email).filter(Boolean)
    );
    
    const quoteOnlySubmissions = submissions.filter(
      sub => !customerEmailsWithAppointments.has(sub.email)
    );
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Quote Requests ({quoteOnlySubmissions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {quoteOnlySubmissions.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg">No quote requests</p>
              <p className="text-sm">Quote requests from people without appointments will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {quoteOnlySubmissions.slice(0, 10).map((submission) => (
              <div 
                key={submission.id} 
                className="border-2 border-stone-200 rounded-lg p-4 hover:border-green-500 transition-all cursor-pointer"
                onClick={() => {
                  setSelectedQuote(submission);
                  setShowQuoteDetailsDialog(true);
                }}
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-lg text-stone-900 break-words">{submission.name}</p>
                    <div className="flex items-center text-stone-600 mt-2 space-y-1 flex-col items-start">
                      <div className="flex items-center min-w-0 w-full">
                        <Phone className="h-4 w-4 mr-2 text-green-700 flex-shrink-0" />
                        <a href={`tel:${submission.phone}`} className="hover:text-green-700 break-all" onClick={(e) => e.stopPropagation()}>
                          {submission.phone}
                        </a>
                      </div>
                      <div className="flex items-center min-w-0 w-full">
                        <Mail className="h-4 w-4 mr-2 text-green-700 flex-shrink-0" />
                        <a href={`mailto:${submission.email}`} className="hover:text-green-700 break-all truncate" onClick={(e) => e.stopPropagation()}>
                          {submission.email}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-stone-600 mb-2">
                      <strong>Submitted:</strong> {new Date(submission.submitted_at).toLocaleString()}
                    </p>
                    <p className="text-stone-700 break-words">
                      <strong>Message:</strong> {submission.message || 'No message provided'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
  };

  const renderCustomers = () => {
    // Only show customers who have had appointments (not just quote requests)
    const customersWithAppointments = new Map();
    
    appointments.forEach(apt => {
      const uniqueKey = customerKey(apt);
      
      if (!customersWithAppointments.has(uniqueKey)) {
        customersWithAppointments.set(uniqueKey, {
          name: apt.customer_name,
          email: apt.customer_email || '',
          phone: apt.customer_phone || '',
          address: apt.customer_address,  // Use appointment address
          location: apt.customer_address,  // Set location to appointment address
          id: apt.id,
          is_recurring: apt.is_recurring || false,
          recurrence_frequency: apt.recurrence_frequency || '',
          appointments: [],
          quotes: []
        });
      }
      // Update recurring info if this appointment has it
      const existing = customersWithAppointments.get(uniqueKey);
      if (apt.is_recurring) {
        existing.is_recurring = true;
        if (apt.recurrence_frequency) existing.recurrence_frequency = apt.recurrence_frequency;
      }
      customersWithAppointments.get(uniqueKey).appointments.push(apt);
    });
    
    // Add quote history for customers who have had appointments
    submissions.forEach(s => {
      const key = (s.email || '').trim().toLowerCase();
      if (key && customersWithAppointments.has(key)) {
        customersWithAppointments.get(key).quotes.push(s);
      }
    });
    
    const customers = Array.from(customersWithAppointments.values());
    
    // Filter customers based on search query (case-insensitive partial match)
    const filteredCustomers = customers.filter(customer => 
      customer.name.toLowerCase().includes(customerSearchQuery.toLowerCase())
    );
    
    // Get recurring customers from appointments
    const recurringCustomersMap = new Map();
    appointments.forEach(apt => {
      if (apt.is_recurring && apt.recurrence_frequency) {
        // Use appointment ID as unique key instead of email (since email is now optional)
        if (!recurringCustomersMap.has(customerKey(apt))) {
          recurringCustomersMap.set(customerKey(apt), {
            name: apt.customer_name,
            email: apt.customer_email || '',
            phone: apt.customer_phone || '',
            frequency: apt.recurrence_frequency,
            active: apt.recurring_active !== false, // Default to true if not set
            appointmentId: apt.id,
            price: apt.price || ''  // Add price field
          });
        }
      }
    });
    
    // Sort: active customers first, then inactive
    const recurringCustomers = Array.from(recurringCustomersMap.values()).sort((a, b) => {
      if (a.active === b.active) return 0;
      return a.active ? -1 : 1; // Active (true) comes before inactive (false)
    });
    
    // Filter recurring customers based on search query
    const filteredRecurringCustomers = recurringCustomers.filter(customer =>
      customer.name.toLowerCase().includes(recurringCustomerSearchQuery.toLowerCase())
    );
    
    return (
      <div className="space-y-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* All Customers - Left Side */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">Customers ({filteredCustomers.length})</CardTitle>
                <Button
                  size="sm"
                  className="bg-green-700 hover:bg-green-800"
                  onClick={() => setShowAddCustomerDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </div>
              {/* Search Bar */}
              <div className="mt-4">
                <Input
                  type="text"
                  placeholder="Search customers by name..."
                  value={customerSearchQuery}
                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
            </CardHeader>
            <CardContent>
              {filteredCustomers.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <User className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p className="text-lg">
                    {customerSearchQuery ? 'No customers found' : 'No customers yet'}
                  </p>
                  <p className="text-sm">
                    {customerSearchQuery ? 'Try a different search term' : 'Customers appear here after their first appointment'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCustomers.map((customer, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border-2 border-stone-200 rounded-lg hover:border-green-500 transition-all">
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="h-12 w-12 rounded-full bg-lime-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-6 w-6 text-lime-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-stone-900 break-words">{customer.name}</p>
                          <p className="text-sm text-stone-600 break-all truncate">{customer.email}</p>
                          <p className="text-sm text-stone-500">{customer.phone}</p>
                          <p className="text-xs text-stone-400">{customer.appointments.length} appointment{customer.appointments.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-green-700 text-green-700 hover:bg-lime-50 ml-2 flex-shrink-0"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setShowCustomerDetailsDialog(true);
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recurring Customers - Right Side */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">Recurring Customers ({filteredRecurringCustomers.length})</CardTitle>
                <Button
                  size="sm"
                  className="bg-stone-700 hover:bg-stone-800"
                  onClick={() => setShowAddRecurringCustomerDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Recurring Customer
                </Button>
              </div>
              {/* Search Bar */}
              <div className="mt-4">
                <Input
                  type="text"
                  placeholder="Search recurring customers..."
                  value={recurringCustomerSearchQuery}
                  onChange={(e) => setRecurringCustomerSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
            </CardHeader>
            <CardContent>
              {filteredRecurringCustomers.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <Repeat className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p className="text-lg">
                    {recurringCustomerSearchQuery ? 'No recurring customers found' : 'No recurring customers yet'}
                  </p>
                  <p className="text-sm">
                    {recurringCustomerSearchQuery ? 'Try a different search term' : 'Customers with recurring appointments will appear here'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredRecurringCustomers.map((customer, index) => (
                  <div 
                    key={index} 
                    className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                      customer.active 
                        ? 'border-lime-200 bg-lime-50 hover:border-green-400' 
                        : 'border-red-200 bg-red-50 hover:border-red-400'
                    }`}
                    onClick={() => {
                      setSelectedRecurringCustomer(customer);
                      setEditingRecurringFrequency(customer.frequency);
                      setEditingRecurringPrice(customer.price || '');
                      setShowRecurringCustomerDialog(true);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          customer.active ? 'bg-lime-200' : 'bg-red-200'
                        }`}>
                          <Repeat className={`h-6 w-6 ${customer.active ? 'text-green-700' : 'text-red-700'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-stone-900 break-words">{customer.name}</p>
                          {customer.email && <p className="text-sm text-stone-600 break-all truncate">{customer.email}</p>}
                          {customer.phone && <p className="text-sm text-stone-600">{customer.phone}</p>}
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              customer.active 
                                ? 'bg-green-600 text-white' 
                                : 'bg-red-600 text-white'
                            }`}>
                              {customer.frequency}
                            </div>
                            {customer.price && (
                              <div className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-lime-100 text-lime-800">
                                ${customer.price}
                              </div>
                            )}
                            <span className={`text-xs font-medium ${
                              customer.active ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {customer.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div 
                        className="flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleRecurringStatus(customer, customer.active);
                        }}
                      >
                        <button
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            customer.active ? 'bg-green-600' : 'bg-red-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              customer.active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    const settingsTabs = [
      { id: 'account', label: 'Account', icon: User },
      { id: 'business', label: 'Business Profile', icon: Building2 },
      { id: 'gallery', label: 'Gallery Photos', icon: Image },
      { id: 'reviews', label: 'Reviews', icon: Star },
      { id: 'notifications', label: 'Notifications', icon: Bell },
      { id: 'hours', label: 'Working Hours', icon: Clock },
      { id: 'security', label: 'Security', icon: Shield },
    ];

    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const toggleDay = (day) => {
      const days = businessSettings.working_hours?.days || [];
      const updated = days.includes(day) ? days.filter(d => d !== day) : [...days, day];
      setBusinessSettings({
        ...businessSettings,
        working_hours: { ...businessSettings.working_hours, days: updated }
      });
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Sidebar */}
        <Card className="lg:col-span-1 border border-stone-200">
          <CardContent className="p-4">
            <nav className="space-y-1">
              {settingsTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    data-testid={`settings-tab-${tab.id}`}
                    onClick={() => setSettingsTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      settingsTab === tab.id
                        ? 'bg-lime-50 text-green-700 border border-lime-200'
                        : 'text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Settings Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Account Tab */}
          {settingsTab === 'account' && (
            <Card className="border border-stone-200">
              <CardHeader className="border-b border-stone-100 bg-stone-50/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-lime-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Account Information</CardTitle>
                    <p className="text-sm text-stone-500">Your personal account details</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-stone-500 mb-1.5 block">Username</Label>
                    <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-800 font-medium">
                      {user?.username}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-stone-500 mb-1.5 block">Display Name</Label>
                    <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-800 font-medium">
                      {user?.name}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-stone-500 mb-1.5 block">Role</Label>
                  <div className="inline-flex items-center gap-2 bg-lime-50 border border-lime-200 text-green-700 px-4 py-2 rounded-full text-sm font-medium capitalize">
                    <Shield className="h-4 w-4" />
                    {user?.role}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Business Profile Tab */}
          {settingsTab === 'business' && (
            <Card className="border border-stone-200">
              <CardHeader className="border-b border-stone-100 bg-stone-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-lime-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-lime-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Business Profile</CardTitle>
                      <p className="text-sm text-stone-500">Company information displayed on your site</p>
                    </div>
                  </div>
                  <Button
                    data-testid="save-business-settings-btn"
                    onClick={saveSettings}
                    disabled={savingSettings}
                    className="bg-green-700 hover:bg-green-800 rounded-xl"
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingSettings ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Business Name</Label>
                    <Input
                      data-testid="business-name-input"
                      value={businessSettings.business_name || ''}
                      onChange={(e) => setBusinessSettings({...businessSettings, business_name: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Phone Number</Label>
                    <Input
                      data-testid="business-phone-input"
                      value={businessSettings.business_phone || ''}
                      onChange={(e) => setBusinessSettings({...businessSettings, business_phone: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Email</Label>
                    <Input
                      data-testid="business-email-input"
                      value={businessSettings.business_email || ''}
                      onChange={(e) => setBusinessSettings({...businessSettings, business_email: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Address</Label>
                    <Input
                      data-testid="business-address-input"
                      value={businessSettings.business_address || ''}
                      onChange={(e) => setBusinessSettings({...businessSettings, business_address: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Service Areas</Label>
                  <p className="text-xs text-stone-500 mb-2">Comma-separated list of cities you serve</p>
                  <Input
                    data-testid="service-areas-input"
                    value={(businessSettings.default_service_areas || []).join(', ')}
                    onChange={(e) => setBusinessSettings({
                      ...businessSettings,
                      default_service_areas: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                    className="rounded-xl"
                    placeholder="Oklahoma City, Edmond, Midwest City..."
                  />
                </div>
              </CardContent>
            </Card>
          )}


          {/* Gallery Tab */}
          {settingsTab === 'gallery' && (
            <Card className="border border-stone-200">
              <CardHeader className="border-b border-stone-100 bg-stone-50/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-lime-100 flex items-center justify-center">
                    <Image className="h-5 w-5 text-lime-700" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Gallery Photos</CardTitle>
                    <p className="text-sm text-stone-500">Manage photos on your home page. New photos are added alongside existing ones.</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Add Photo Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Upload from Camera Roll */}
                  <div className="p-5 bg-lime-50 border-2 border-dashed border-green-300 rounded-xl text-center">
                    <Image className="h-10 w-10 mx-auto mb-3 text-green-600" />
                    <p className="font-medium text-stone-800 text-sm mb-2">Upload from Camera Roll</p>
                    <p className="text-xs text-stone-500 mb-3">JPG, PNG, WEBP supported</p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="photo-upload-input"
                        onChange={handleUploadPhoto}
                        disabled={uploadingPhoto}
                      />
                      <span className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        uploadingPhoto
                          ? 'bg-stone-300 text-stone-500 cursor-wait'
                          : 'bg-green-700 text-white hover:bg-green-800 cursor-pointer'
                      }`}>
                        <Plus className="h-4 w-4" />
                        {uploadingPhoto ? 'Uploading...' : 'Choose Photo'}
                      </span>
                    </label>
                  </div>

                  {/* Paste URL */}
                  <div className="p-5 bg-stone-50 border border-stone-200 rounded-xl space-y-3">
                    <p className="font-medium text-stone-800 text-sm">Or Paste Image URL</p>
                    <Input
                      data-testid="new-photo-url-input"
                      value={newPhotoUrl}
                      onChange={(e) => setNewPhotoUrl(e.target.value)}
                      placeholder="https://..."
                      className="rounded-xl"
                    />
                    <Input
                      data-testid="new-photo-caption-input"
                      value={newPhotoCaption}
                      onChange={(e) => setNewPhotoCaption(e.target.value)}
                      placeholder="Caption (optional)"
                      className="rounded-xl"
                    />
                    <Button
                      data-testid="add-photo-btn"
                      onClick={handleAddPhoto}
                      className="bg-green-700 hover:bg-green-800 rounded-xl"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Photo
                    </Button>
                  </div>
                </div>

                {/* Photo Grid with Reorder */}
                {galleryPhotos.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-stone-700 mb-3">Your Photos ({galleryPhotos.length}) — Use arrows to reorder</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {galleryPhotos.map((photo, idx) => (
                        <div key={photo.id} className="relative group rounded-xl overflow-hidden border border-stone-200">
                          <img
                            src={photo.url}
                            alt={photo.caption || 'Gallery photo'}
                            className="w-full h-40 object-cover"
                          />
                          {/* Reorder + Delete overlay */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white text-white hover:bg-white/20 rounded-lg h-8 w-8 p-0"
                                onClick={() => handleReorderPhoto(photo.id, 'up')}
                                disabled={idx === 0}
                              >
                                <ChevronDown className="h-4 w-4 rotate-180" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white text-white hover:bg-white/20 rounded-lg h-8 w-8 p-0"
                                onClick={() => handleReorderPhoto(photo.id, 'down')}
                                disabled={idx === galleryPhotos.length - 1}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button
                              data-testid={`delete-photo-${photo.id}`}
                              size="sm"
                              variant="outline"
                              className="border-red-400 text-red-300 hover:bg-red-500/20 rounded-lg"
                              onClick={() => handleDeletePhoto(photo.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                          {photo.caption && (
                            <div className="p-2 bg-white">
                              <p className="text-xs text-stone-600 truncate">{photo.caption}</p>
                            </div>
                          )}
                          <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                            #{idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {galleryPhotos.length === 0 && (
                  <div className="text-center py-8 text-stone-400">
                    <Image className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No custom photos added yet.</p>
                    <p className="text-xs mt-1">Your default gallery photos will show on the home page. Add more above!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Reviews Tab */}
          {settingsTab === 'reviews' && (
            <Card className="border border-stone-200">
              <CardHeader className="border-b border-stone-100 bg-stone-50/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Star className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Customer Reviews</CardTitle>
                    <p className="text-sm text-stone-500">Manage reviews displayed on your home page</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Add Review Form */}
                <div className="p-5 bg-stone-50 border border-stone-200 rounded-xl space-y-3">
                  <p className="font-medium text-stone-800 text-sm">
                    {editingReview ? 'Edit Review' : 'Add New Review'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      data-testid="review-name-input"
                      value={editingReview ? editingReview.name : newReview.name}
                      onChange={(e) => editingReview 
                        ? setEditingReview({...editingReview, name: e.target.value})
                        : setNewReview({...newReview, name: e.target.value})
                      }
                      placeholder="Customer name *"
                      className="rounded-xl"
                    />
                    <Input
                      data-testid="review-location-input"
                      value={editingReview ? editingReview.location : newReview.location}
                      onChange={(e) => editingReview
                        ? setEditingReview({...editingReview, location: e.target.value})
                        : setNewReview({...newReview, location: e.target.value})
                      }
                      placeholder="City (e.g. Edmond)"
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">Rating</label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(star => (
                        <button
                          key={star}
                          data-testid={`review-star-${star}`}
                          onClick={() => editingReview
                            ? setEditingReview({...editingReview, rating: star})
                            : setNewReview({...newReview, rating: star})
                          }
                          className="transition-colors"
                        >
                          <Star className={`h-6 w-6 ${
                            star <= (editingReview ? editingReview.rating : newReview.rating)
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-stone-300'
                          }`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea
                    data-testid="review-text-input"
                    value={editingReview ? editingReview.text : newReview.text}
                    onChange={(e) => editingReview
                      ? setEditingReview({...editingReview, text: e.target.value})
                      : setNewReview({...newReview, text: e.target.value})
                    }
                    placeholder="Review text *"
                    rows={3}
                    className="rounded-xl"
                  />
                  <div className="flex gap-2">
                    {editingReview ? (
                      <>
                        <Button
                          data-testid="save-review-btn"
                          onClick={handleUpdateReview}
                          className="bg-green-700 hover:bg-green-800 rounded-xl"
                          size="sm"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setEditingReview(null)}
                          className="rounded-xl"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        data-testid="add-review-btn"
                        onClick={handleAddReview}
                        className="bg-green-700 hover:bg-green-800 rounded-xl"
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Review
                      </Button>
                    )}
                  </div>
                </div>

                {/* Reviews List */}
                <div className="space-y-3">
                  {siteReviews.map((review) => (
                    <div key={review.id} className="p-4 bg-white border border-stone-200 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-stone-800 text-sm">{review.name}</p>
                            {review.location && (
                              <span className="text-xs text-stone-400">- {review.location}</span>
                            )}
                          </div>
                          <div className="flex gap-0.5 mb-2">
                            {[...Array(review.rating || 5)].map((_, i) => (
                              <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                            ))}
                          </div>
                          <p className="text-sm text-stone-600 italic">"{review.text}"</p>
                        </div>
                        <div className="flex gap-1 ml-3">
                          <Button
                            data-testid={`edit-review-${review.id}`}
                            size="sm"
                            variant="ghost"
                            className="text-stone-400 hover:text-lime-700 h-8 w-8 p-0"
                            onClick={() => setEditingReview(review)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-review-${review.id}`}
                            size="sm"
                            variant="ghost"
                            className="text-stone-400 hover:text-red-600 h-8 w-8 p-0"
                            onClick={() => handleDeleteReview(review.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {siteReviews.length === 0 && (
                  <div className="text-center py-8 text-stone-400">
                    <Star className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No reviews added yet. Add your first review above.</p>
                    <p className="text-xs mt-1">Reviews will appear on your home page.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notifications Tab */}
          {settingsTab === 'notifications' && (
            <Card className="border border-stone-200">
              <CardHeader className="border-b border-stone-100 bg-stone-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Bell className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Notifications</CardTitle>
                      <p className="text-sm text-stone-500">Control how you receive alerts</p>
                    </div>
                  </div>
                  <Button
                    data-testid="save-notification-settings-btn"
                    onClick={saveSettings}
                    disabled={savingSettings}
                    className="bg-green-700 hover:bg-green-800 rounded-xl"
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingSettings ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* New Quote Requests */}
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-800 text-sm">New Quote Requests</p>
                      <p className="text-xs text-stone-500 mt-0.5">Get an email when someone submits a quote request</p>
                    </div>
                    <button
                      data-testid="toggle-email_on_new_quote"
                      onClick={() => setBusinessSettings({
                        ...businessSettings,
                        notifications: {
                          ...businessSettings.notifications,
                          email_on_new_quote: !businessSettings.notifications?.email_on_new_quote
                        }
                      })}
                      className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                        businessSettings.notifications?.email_on_new_quote ? 'bg-green-600' : 'bg-stone-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        businessSettings.notifications?.email_on_new_quote ? 'translate-x-6' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">Send notifications to</label>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      data-testid="quote-notification-email-input"
                      value={businessSettings.notifications?.quote_email || ''}
                      onChange={(e) => setBusinessSettings({
                        ...businessSettings,
                        notifications: {
                          ...businessSettings.notifications,
                          quote_email: e.target.value
                        }
                      })}
                      className="bg-white border-stone-300 rounded-lg h-10 text-sm"
                    />
                    <p className="text-[11px] text-stone-400 mt-1">Leave blank to use the default business email.</p>
                  </div>
                </div>

                {/* New Bookings */}
                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div>
                    <p className="font-medium text-stone-800 text-sm">New Bookings</p>
                    <p className="text-xs text-stone-500 mt-0.5">Get notified when a new appointment is booked</p>
                  </div>
                  <button
                    data-testid="toggle-email_on_booking"
                    onClick={() => setBusinessSettings({
                      ...businessSettings,
                      notifications: {
                        ...businessSettings.notifications,
                        email_on_booking: !businessSettings.notifications?.email_on_booking
                      }
                    })}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                      businessSettings.notifications?.email_on_booking ? 'bg-green-600' : 'bg-stone-300'
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      businessSettings.notifications?.email_on_booking ? 'translate-x-6' : 'translate-x-0'
                    }`} />
                  </button>
                </div>

                {/* SMS Notifications */}
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-800 text-sm">SMS Notifications</p>
                      <p className="text-xs text-stone-500 mt-0.5">Receive text message alerts (requires Twilio setup)</p>
                    </div>
                    <button
                      data-testid="toggle-sms_notifications"
                      onClick={() => setBusinessSettings({
                        ...businessSettings,
                        notifications: {
                          ...businessSettings.notifications,
                          sms_notifications: !businessSettings.notifications?.sms_notifications
                        }
                      })}
                      className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                        businessSettings.notifications?.sms_notifications ? 'bg-green-600' : 'bg-stone-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        businessSettings.notifications?.sms_notifications ? 'translate-x-6' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">Send SMS to</label>
                    <Input
                      type="tel"
                      placeholder="(555) 123-4567"
                      data-testid="sms-notification-phone-input"
                      value={businessSettings.notifications?.sms_phone || ''}
                      onChange={(e) => setBusinessSettings({
                        ...businessSettings,
                        notifications: {
                          ...businessSettings.notifications,
                          sms_phone: e.target.value
                        }
                      })}
                      className="bg-white border-stone-300 rounded-lg h-10 text-sm"
                    />
                    <p className="text-[11px] text-stone-400 mt-1">Phone number that will receive SMS alerts.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Working Hours Tab */}
          {settingsTab === 'hours' && (
            <Card className="border border-stone-200">
              <CardHeader className="border-b border-stone-100 bg-stone-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-stone-100 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-stone-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Working Hours</CardTitle>
                      <p className="text-sm text-stone-500">Set your default availability</p>
                    </div>
                  </div>
                  <Button
                    data-testid="save-hours-settings-btn"
                    onClick={saveSettings}
                    disabled={savingSettings}
                    className="bg-green-700 hover:bg-green-800 rounded-xl"
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingSettings ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Start Time</Label>
                    <Input
                      data-testid="start-time-input"
                      type="time"
                      value={businessSettings.working_hours?.start || '08:00'}
                      onChange={(e) => setBusinessSettings({
                        ...businessSettings,
                        working_hours: { ...businessSettings.working_hours, start: e.target.value }
                      })}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">End Time</Label>
                    <Input
                      data-testid="end-time-input"
                      type="time"
                      value={businessSettings.working_hours?.end || '18:00'}
                      onChange={(e) => setBusinessSettings({
                        ...businessSettings,
                        working_hours: { ...businessSettings.working_hours, end: e.target.value }
                      })}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-3 block">Working Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {allDays.map(day => (
                      <button
                        key={day}
                        data-testid={`day-toggle-${day.toLowerCase()}`}
                        onClick={() => toggleDay(day)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                          (businessSettings.working_hours?.days || []).includes(day)
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security Tab */}
          {settingsTab === 'security' && (
            <Card className="border border-stone-200">
              <CardHeader className="border-b border-stone-100 bg-stone-50/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-red-700" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Security</CardTitle>
                    <p className="text-sm text-stone-500">Manage your password and access</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-5">
                {(user?.role === 'admin' || user?.role === 'owner') && (
                  <div className="p-5 bg-stone-50 border border-stone-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-stone-800">Password</p>
                        <p className="text-sm text-stone-500 mt-0.5">Last changed: Unknown</p>
                      </div>
                      <Button
                        data-testid="change-password-btn"
                        onClick={() => setShowChangePasswordDialog(true)}
                        variant="outline"
                        className="border-stone-300 rounded-xl"
                      >
                        Change Password
                      </Button>
                    </div>
                  </div>
                )}
                <div className="p-5 bg-stone-50 border border-stone-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-800">Active Sessions</p>
                      <p className="text-sm text-stone-500 mt-0.5">Currently signed in on this device</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 bg-lime-50 text-green-700 text-xs font-medium px-3 py-1 rounded-full border border-lime-200">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-5 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-red-800">Sign Out Everywhere</p>
                      <p className="text-sm text-red-600 mt-0.5">Log out of all devices and sessions</p>
                    </div>
                    <Button
                      data-testid="logout-all-btn"
                      onClick={handleLogout}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-100 rounded-xl"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out All
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  // Role-based permissions helper
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner';
  
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'quotes':
        return renderQuotes();
      case 'schedule':
        return renderSchedule();
      case 'customers':
        return renderCustomers();
      case 'employees':
        // Only admins and owners can view employees
        return isAdminOrOwner ? renderEmployees() : (
          <div className="text-center py-12">
            <p className="text-lg text-stone-600">Access Restricted</p>
            <p className="text-sm text-stone-500">You don't have permission to view this section</p>
          </div>
        );
      case 'settings':
        return renderSettings();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-lime-50 flex" style={{ fontFamily: "'Work Sans', sans-serif" }}>
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-stone-900/40 z-40 md:hidden" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-overlay" />
      )}
      {/* Sidebar */}
      <aside data-testid="dashboard-sidebar" className={`fixed md:relative inset-y-0 left-0 z-50 overflow-y-auto overflow-x-hidden w-64 shrink-0 bg-white border-r border-lime-200 min-h-screen flex flex-col transition-transform duration-300 md:translate-x-0 ${mobileNavOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-stone-200">
          <img 
            src="/images/logo.webp" 
            alt="Walker Lawn Care Logo" 
            className="h-14 w-auto object-contain" data-testid="dashboard-sidebar-logo"
          />
        </div>

        <div className="p-4 border-b border-stone-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-green-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {(user?.name || user?.username || 'U')[0].toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-stone-900 text-sm">{user?.name || user?.username}</p>
              <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="p-3 flex-1">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    data-testid={`nav-${item.id}`}
                    onClick={() => { setActiveSection(item.id); setMobileNavOpen(false); }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all text-sm ${
                      activeSection === item.id
                        ? 'bg-green-600 text-white shadow-md shadow-green-600/20'
                        : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="p-3">
          <Button 
            onClick={handleLogout}
            variant="ghost"
            data-testid="sidebar-logout-btn"
            className="w-full text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-xl justify-start"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Logout
          </Button>
        </div>
        <div className="h-10" />
        <GrassDivider color="#9ccc65" />
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
          <div className="px-4 md:px-8 py-4 md:py-5">
            <div className="flex justify-between items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                data-testid="mobile-nav-toggle"
                aria-label="Open menu"
                className="md:hidden h-10 w-10 shrink-0 rounded-xl bg-green-600 text-white flex items-center justify-center"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-stone-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {menuItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
                </h1>
                <p className="text-sm text-stone-500 mt-0.5">Welcome back, {user?.name || user?.username}!</p>
              </div>
              <Button 
                onClick={handleLogout}
                variant="outline"
                data-testid="header-logout-btn"
                className="hidden sm:inline-flex border-stone-300 text-stone-600 hover:bg-stone-50 rounded-xl"
                size="sm"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Log out
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6">
          {renderContent()}
        </main>
      </div>

      {/* Edit Employee Dialog */}
      <Dialog open={showEditEmployeeDialog} onOpenChange={setShowEditEmployeeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEmployee} className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                required
                value={editEmployee.name}
                onChange={(e) => setEditEmployee({...editEmployee, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                required
                type="tel"
                value={editEmployee.phone}
                onChange={(e) => setEditEmployee({...editEmployee, phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                className="w-full border rounded p-2"
                value={editEmployee.role}
                onChange={(e) => setEditEmployee({...editEmployee, role: e.target.value})}
              >
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div>
              <Label>New Password (Leave blank to keep current)</Label>
              <Input
                type="password"
                value={editEmployee.password}
                onChange={(e) => setEditEmployee({...editEmployee, password: e.target.value})}
                placeholder="New password (optional)"
              />
            </div>
            <Button type="submit" className="w-full bg-green-700 hover:bg-green-800">
              Update Employee
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={showEditAppointmentDialog} onOpenChange={setShowEditAppointmentDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateAppointment} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                data-testid="edit-appointment-title-input"
                value={editAppointment.title || ''}
                onChange={(e) => setEditAppointment({...editAppointment, title: e.target.value})}
                placeholder="Lawn Mowing - Smith Residence"
              />
            </div>
            <div>
              <Label>Customer Name</Label>
              <Input
                required
                value={editAppointment.customer_name}
                onChange={(e) => setEditAppointment({...editAppointment, customer_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Customer Phone (Optional)</Label>
              <Input
                value={editAppointment.customer_phone}
                onChange={(e) => setEditAppointment({...editAppointment, customer_phone: e.target.value})}
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                className="w-full border rounded p-2"
                value={editAppointment.status}
                onChange={(e) => setEditAppointment({...editAppointment, status: e.target.value})}
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <Label>Services (Select all that apply)</Label>
              <div className="border rounded p-3 space-y-2">
                {['Lawn Mowing', 'Edging', 'Trimming', 'Flower Bed Maintenance', 'Small Junk Removal'].map((service) => (
                  <label key={service} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editAppointment.services && editAppointment.services.includes(service)}
                      onChange={(e) => {
                        const currentServices = editAppointment.services || [];
                        if (e.target.checked) {
                          setEditAppointment({
                            ...editAppointment,
                            services: [...currentServices, service]
                          });
                        } else {
                          setEditAppointment({
                            ...editAppointment,
                            services: currentServices.filter(s => s !== service)
                          });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{service}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                required
                type="date"
                data-testid="edit-appointment-date-input"
                value={editAppointment.start_time ? editAppointment.start_time.split('T')[0] : ''}
                onChange={(e) => {
                  const time = editAppointment.start_time?.split('T')[1] || '12:00';
                  const dateValue = e.target.value ? `${e.target.value}T${time}` : '';
                  setEditAppointment({
                    ...editAppointment, 
                    start_time: dateValue,
                    end_time: dateValue
                  });
                }}
              />
            </div>
            <div>
              <Label>Assign Employees (Optional)</Label>
              <div className="border rounded p-3 max-h-40 overflow-y-auto">
                {employees.map((emp) => (
                  <label key={emp.id} className="flex items-center space-x-2 mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editAppointment.assigned_employees.includes(emp.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditAppointment({
                            ...editAppointment,
                            assigned_employees: [...editAppointment.assigned_employees, emp.id]
                          });
                        } else {
                          setEditAppointment({
                            ...editAppointment,
                            assigned_employees: editAppointment.assigned_employees.filter(id => id !== emp.id)
                          });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{emp.name} (@{emp.username})</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editAppointment.notes}
                onChange={(e) => setEditAppointment({...editAppointment, notes: e.target.value})}
                placeholder="Special instructions..."
              />
            </div>
            <Button type="submit" data-testid="update-appointment-btn" className="w-full bg-green-700 hover:bg-green-800">
              Update Appointment
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customer Details Dialog */}
      <Dialog open={showCustomerDetailsDialog} onOpenChange={setShowCustomerDetailsDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">Customer Details</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4 sm:space-y-6">
              {/* Contact Information */}
              <div className="bg-lime-50 p-3 sm:p-4 rounded-lg">
                <h3 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3 text-green-900">Contact Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Name</p>
                    <p className="font-semibold break-words text-sm sm:text-base">{selectedCustomer.name || <span className="text-stone-400 italic">(blank)</span>}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Email</p>
                    <p className="font-semibold break-all text-sm sm:text-base">{selectedCustomer.email || <span className="text-stone-400 italic">(blank)</span>}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Phone</p>
                    <p className="font-semibold break-all text-sm sm:text-base">{selectedCustomer.phone || <span className="text-stone-400 italic">(blank)</span>}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Location</p>
                    <p className="font-semibold break-words text-sm sm:text-base">{selectedCustomer.location || <span className="text-stone-400 italic">(blank)</span>}</p>
                  </div>
                </div>
              </div>

              {/* Quote Summary - only show if customer has quotes */}
              {selectedCustomer.quotes && selectedCustomer.quotes.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Quote Request Summary</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-lime-50 p-3 rounded border border-lime-200">
                      <p className="text-sm text-lime-700">Total Requests</p>
                      <p className="text-2xl font-bold text-lime-700">{selectedCustomer.quotes.length}</p>
                    </div>
                    <div className="bg-stone-50 p-3 rounded border border-stone-200">
                      <p className="text-sm text-stone-600">First Contact</p>
                      <p className="text-sm font-semibold text-stone-700">
                        {new Date(selectedCustomer.quotes[selectedCustomer.quotes.length - 1].submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded border border-amber-200">
                      <p className="text-sm text-amber-600">Last Contact</p>
                      <p className="text-sm font-semibold text-amber-700">
                        {new Date(selectedCustomer.quotes[0].submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Customer Button - moved here above Last Contact */}
              {/* Frequency Section - show for recurring customers */}
              {selectedCustomer.is_recurring && (
                <div className="bg-lime-50 p-3 sm:p-4 rounded-lg">
                  <h3 className="font-semibold text-base sm:text-lg mb-2 text-lime-900">Mowing Frequency</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-stone-600">Current:</span>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      selectedCustomer.recurrence_frequency === 'Weekly' 
                        ? 'bg-lime-100 text-lime-800' 
                        : 'bg-stone-100 text-stone-800'
                    }`}>
                      {selectedCustomer.recurrence_frequency || 'Not set'}
                    </span>
                    <select
                      data-testid="frequency-change-select"
                      className="text-sm border rounded px-3 py-1.5 ml-auto"
                      value={selectedCustomer.recurrence_frequency || ''}
                      onChange={(e) => handleChangeFrequencyFromDetails(e.target.value)}
                    >
                      <option value="" disabled>Change frequency</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Bi-Weekly">Bi-Weekly</option>
                    </select>
                  </div>
                </div>
              )}
              <div>
                <Button 
                  className="w-full bg-lime-600 hover:bg-lime-700"
                  onClick={() => handleEditCustomer(selectedCustomer)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Customer
                </Button>
              </div>

              {/* Quote History - only show if customer has quotes */}
              {selectedCustomer.quotes && selectedCustomer.quotes.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Last Contact</h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {selectedCustomer.quotes.map((quote, idx) => (
                      <div key={idx} className="border-l-4 border-green-500 pl-4 py-2 bg-stone-50 rounded">
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-semibold text-sm">{new Date(quote.submitted_at).toLocaleDateString()}</p>
                          <span className="text-xs bg-lime-100 text-green-800 px-2 py-1 rounded">
                            {quote.location || 'Location not specified'}
                          </span>
                        </div>
                        {quote.services && quote.services.length > 0 && (
                          <p className="text-sm text-stone-600 mb-1">
                            <strong>Services:</strong> {quote.services.join(', ')}
                          </p>
                        )}
                        {quote.message && (
                          <p className="text-sm text-stone-700 italic">"{quote.message}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Customer Bookings/Appointments */}
              {selectedCustomer.appointments && selectedCustomer.appointments.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">Customer Bookings ({selectedCustomer.appointments.length})</h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBookAppointmentForCustomer(selectedCustomer)}
                        className="border-green-700 text-green-700 hover:bg-lime-50"
                      >
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        Book Appointment
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCustomerBookings(!showCustomerBookings)}
                        className="border-lime-600 text-lime-700 hover:bg-lime-50"
                      >
                        {showCustomerBookings ? 'Hide Bookings' : 'Show Bookings'}
                      </Button>
                    </div>
                  </div>
                  
                  {showCustomerBookings && (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {selectedCustomer.appointments.map((apt, idx) => (
                        <div key={idx} className="border-l-4 border-lime-600 pl-4 py-2 bg-lime-50 rounded relative">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute top-2 right-2 h-7 w-7 p-0 hover:bg-lime-100"
                            onClick={() => handleEditAppointment(apt)}
                          >
                            <Edit className="h-4 w-4 text-lime-700" />
                          </Button>
                          <div className="flex justify-between items-start mb-1 pr-10">
                            <p className="font-semibold text-sm">
                              {new Date(apt.start_time).toLocaleDateString()}
                            </p>
                            <span className={`text-xs px-2 py-1 rounded border ${STATUS_COLORS[apt.status] || STATUS_COLORS.pending}`}>
                              {apt.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                            </span>
                          </div>
                          {apt.services && apt.services.length > 0 && (
                            <p className="text-sm text-stone-600 mb-1">
                              <strong>Services:</strong> {apt.services.join(', ')}
                            </p>
                          )}
                          {apt.customer_address && (
                            <p className="text-sm text-stone-600 mb-1">
                              <strong>Address:</strong> {apt.customer_address}
                            </p>
                          )}
                          {apt.assigned_employees && apt.assigned_employees.length > 0 && (
                            <p className="text-sm text-stone-600 mb-1">
                              <strong>Assigned:</strong> {apt.assigned_employees.map(empId => getEmployeeName(empId)).join(', ')}
                            </p>
                          )}
                          {apt.is_recurring && (
                            <span className="inline-block mt-1 px-2 py-1 bg-stone-100 text-stone-800 rounded text-xs">
                              Recurring: {apt.recurrence_frequency}
                            </span>
                          )}
                          {apt.notes && (
                            <p className="text-sm text-stone-700 italic mt-1">"{apt.notes}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  className="flex-1 bg-green-700 hover:bg-green-800"
                  onClick={() => window.location.href = `mailto:${selectedCustomer.email}`}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 border-green-700 text-green-700 hover:bg-lime-50"
                  onClick={() => window.location.href = `tel:${selectedCustomer.phone}`}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call Customer
                </Button>
                <Button 
                  variant="outline" 
                  className="border-red-500 text-red-500 hover:bg-red-50"
                  data-testid="delete-customer-btn"
                  onClick={() => handleDeleteCustomer(selectedCustomer.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer Information</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCustomer} className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                required
                value={editCustomer.name}
                onChange={(e) => setEditCustomer({...editCustomer, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editCustomer.email}
                onChange={(e) => setEditCustomer({...editCustomer, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={editCustomer.phone}
                onChange={(e) => setEditCustomer({...editCustomer, phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={editCustomer.location}
                onChange={(e) => setEditCustomer({...editCustomer, location: e.target.value})}
                placeholder="OKC, Edmond, etc."
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-green-700 hover:bg-green-800">
                Save Changes
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowEditCustomerDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="border-red-500 text-red-500 hover:bg-red-50"
                onClick={() => handleDeleteCustomer(editCustomer.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quote Details Dialog */}
      <Dialog open={showQuoteDetailsDialog} onOpenChange={setShowQuoteDetailsDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">Quote Request Details</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4 sm:space-y-6">
              {/* Customer Contact */}
              <div className="bg-lime-50 p-3 sm:p-4 rounded-lg">
                <h3 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3 text-green-900">Customer Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Name</p>
                    <p className="font-semibold break-words text-sm sm:text-base">{selectedQuote.name}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Email</p>
                    <p className="font-semibold break-all text-sm sm:text-base">{selectedQuote.email}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Phone</p>
                    <p className="font-semibold break-all text-sm sm:text-base">{selectedQuote.phone}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-stone-600">Location</p>
                    <p className="font-semibold break-words text-sm sm:text-base">{selectedQuote.location || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div>
                <h3 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3">Request Details</h3>
                <div className="space-y-2 sm:space-y-3">
                  <div>
                    <p className="text-xs sm:text-sm text-stone-600">Submitted</p>
                    <p className="font-semibold text-sm sm:text-base">
                      {new Date(selectedQuote.submitted_at).toLocaleDateString()} at {new Date(selectedQuote.submitted_at).toLocaleTimeString()}
                    </p>
                  </div>

                  {selectedQuote.services && selectedQuote.services.length > 0 && (
                    <div>
                      <p className="text-xs sm:text-sm text-stone-600 mb-2">Services Requested</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedQuote.services.map((service, idx) => (
                          <span key={idx} className="px-2 sm:px-3 py-1 bg-lime-100 text-green-800 rounded-full text-xs sm:text-sm">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedQuote.message && (
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-stone-600 mb-1">Message</p>
                      <div className="bg-stone-50 p-3 sm:p-4 rounded-lg">
                        <p className="text-xs sm:text-base text-stone-900 break-words whitespace-pre-wrap">{selectedQuote.message}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t">
                <Button 
                  className="w-full sm:flex-1 bg-green-700 hover:bg-green-800"
                  onClick={() => {
                    handleBookAppointmentForCustomer(selectedQuote);
                    setShowQuoteDetailsDialog(false);
                  }}
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Book Appointment
                </Button>
                <Button 
                  variant="outline"
                  className="w-full sm:flex-1 border-green-700 text-green-700 hover:bg-lime-50"
                  onClick={() => window.location.href = `mailto:${selectedQuote.email}`}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full sm:flex-1 border-green-700 text-green-700 hover:bg-lime-50"
                  onClick={() => window.location.href = `tel:${selectedQuote.phone}`}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto border-red-500 text-red-500 hover:bg-red-50"
                  onClick={() => handleDeleteQuote(selectedQuote.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recurring Customer Management Dialog */}
      <Dialog open={showRecurringCustomerDialog} onOpenChange={setShowRecurringCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl">Manage Recurring Customer</DialogTitle>
          </DialogHeader>
          {selectedRecurringCustomer && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-lime-50 p-4 rounded-lg">
                <p className="font-semibold text-lg">{selectedRecurringCustomer.name || <span className="text-stone-400 italic">(blank)</span>}</p>
                <p className="text-sm text-stone-600">{selectedRecurringCustomer.email || <span className="text-stone-400 italic">(blank)</span>}</p>
                <p className="text-sm text-stone-600">{selectedRecurringCustomer.phone || <span className="text-stone-400 italic">(blank)</span>}</p>
                <div className="mt-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    selectedRecurringCustomer.active 
                      ? 'bg-green-600 text-white' 
                      : 'bg-red-600 text-white'
                  }`}>
                    {selectedRecurringCustomer.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Change Frequency */}
              <div>
                <Label>Change Frequency</Label>
                <select
                  className="w-full mt-1 px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={editingRecurringFrequency}
                  onChange={(e) => setEditingRecurringFrequency(e.target.value)}
                >
                  <option value="Weekly">Weekly</option>
                  <option value="Bi-weekly">Bi-weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                </select>
              </div>

              {/* Price */}
              <div>
                <Label>Price</Label>
                <Input
                  type="text"
                  value={editingRecurringPrice}
                  onChange={(e) => setEditingRecurringPrice(e.target.value)}
                  placeholder="65"
                  className="mt-1"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  className="flex-1 bg-green-700 hover:bg-green-800"
                  onClick={handleChangeRecurringFrequency}
                >
                  Save Changes
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 border-red-500 text-red-500 hover:bg-red-50"
                  onClick={handleCancelRecurring}
                >
                  Cancel Recurring
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                required
                minLength={6}
              />
              <p className="text-xs text-stone-500 mt-1">Minimum 6 characters</p>
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                required
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-lime-600 hover:bg-lime-700">
                Update Password
              </Button>
              <Button 
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowChangePasswordDialog(false);
                  setPasswordForm({currentPassword: '', newPassword: '', confirmPassword: ''});
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={showAddCustomerDialog} onOpenChange={setShowAddCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCustomer} className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                required
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone (Optional)</Label>
              <Input
                type="tel"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                required
                value={newCustomer.location}
                onChange={(e) => setNewCustomer({...newCustomer, location: e.target.value})}
                placeholder="OKC, Edmond, etc."
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-green-700 hover:bg-green-800">
                Add Customer
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAddCustomerDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Recurring Customer Dialog */}
      <Dialog open={showAddRecurringCustomerDialog} onOpenChange={setShowAddRecurringCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Recurring Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddRecurringCustomer} className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                required
                value={newRecurringCustomer.name}
                onChange={(e) => setNewRecurringCustomer({...newRecurringCustomer, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={newRecurringCustomer.email}
                onChange={(e) => setNewRecurringCustomer({...newRecurringCustomer, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone (Optional)</Label>
              <Input
                type="tel"
                value={newRecurringCustomer.phone}
                onChange={(e) => setNewRecurringCustomer({...newRecurringCustomer, phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={newRecurringCustomer.location}
                onChange={(e) => setNewRecurringCustomer({...newRecurringCustomer, location: e.target.value})}
                placeholder="OKC, Edmond, etc."
              />
            </div>
            <div>
              <Label>Frequency</Label>
              <select
                className="w-full border rounded p-2"
                value={newRecurringCustomer.frequency}
                onChange={(e) => setNewRecurringCustomer({...newRecurringCustomer, frequency: e.target.value})}
              >
                <option value="Weekly">Weekly</option>
                <option value="Bi-Weekly">Bi-Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </div>
            <div>
              <Label>Price</Label>
              <Input
                type="text"
                value={newRecurringCustomer.price}
                onChange={(e) => setNewRecurringCustomer({...newRecurringCustomer, price: e.target.value})}
                placeholder="65"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-stone-700 hover:bg-stone-800">
                Add Recurring Customer
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAddRecurringCustomerDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Mowing Order Dialog */}
      <Dialog open={showMowingOrderDialog} onOpenChange={setShowMowingOrderDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-xl sm:text-2xl">Mowing Schedule</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingMowingOrder(!editingMowingOrder)}
                className="border-lime-600 text-lime-700 hover:bg-lime-50"
              >
                {editingMowingOrder ? 'Done Editing' : 'Edit Schedule'}
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">Weekly & Bi-Weekly Customer Schedule - Organized by mowing week</p>
            
            {/* Editable Mowing Schedule List */}
            <div className="border rounded-lg p-4 bg-stone-50">
              
              {/* 1st Schedule - Friday */}
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 text-lime-900 bg-lime-100 px-3 py-2 rounded">
                  1st Schedule - Friday
                </h3>
                <div className="space-y-1 text-sm ml-2">
                  {scheduleCustomers['1st'].map((customer, index) => (
                    <div key={customer.id} className="py-1 flex items-center justify-between group border-b border-stone-200">
                      <div className="flex items-center gap-2 flex-1">
                        {editingMowingOrder && (
                          <div className="flex flex-col">
                            <button
                              onClick={() => moveCustomerUp('1st', index)}
                              disabled={index === 0}
                              className="text-stone-600 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              <ChevronDown className="h-3 w-3 rotate-180" />
                            </button>
                            <button
                              onClick={() => moveCustomerDown('1st', index)}
                              disabled={index === scheduleCustomers['1st'].length - 1}
                              className="text-stone-600 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-medium">
                            {customer.customer_name} - ${customer.price || '?'}
                            {customer.recurrence_frequency === 'Weekly' && (
                              <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Weekly</span>
                            )}
                            {customer.recurrence_frequency === 'Bi-Weekly' && (
                              <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span>
                            )}
                          </div>
                          <div className="text-xs text-stone-600">{customer.customer_address}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingMowingOrder && (
                          <>
                            <select
                              className="text-xs border rounded px-2 py-1"
                              onChange={(e) => {
                                if (e.target.value && e.target.value !== '1st') {
                                  moveCustomerToSchedule(customer, '1st', e.target.value);
                                }
                                e.target.value = '1st';
                              }}
                              defaultValue="1st"
                            >
                              <option value="1st">1st</option>
                              <option value="2nd">→ 2nd</option>
                              <option value="3rd">→ 3rd</option>
                              <option value="4th">→ 4th</option>
                            </select>
                            <button
                              onClick={() => removeFromRecurring(customer.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove from recurring"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => viewCustomerFromMowingOrder(customer.customer_name)}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Add Customer Button */}
                <div className="mt-3 ml-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-dashed border-lime-400 text-lime-700 hover:bg-lime-50"
                    onClick={() => {
                      setSelectedScheduleSection('1st Schedule - Friday');
                      setShowAddRecurringCustomerDialog(true);
                      setShowMowingOrderDialog(false);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to 1st Schedule
                  </Button>
                </div>
              </div>

              {/* 2nd Schedule - Thursday */}
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 text-stone-900 bg-stone-100 px-3 py-2 rounded">
                  2nd Schedule - Thursday
                </h3>
                <div className="space-y-1 text-sm ml-2">
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Angie ohman - Pennsylvania - $40 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">1608 N Pennsylvania</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Angie ohman - Pennsylvania')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Angie ohman - Jordan - $60 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">1800 N Jordan ave</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Angie ohman - Jordan')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Angie ohman - NE 14th - $60 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">1612 NE 14th st</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Angie ohman - NE 14th')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Keith - $60 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">1608 NE 14th st</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Keith')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Gay dude - $50 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">9124 snowberry drive</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Gay dude')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Miya - $50 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">3305 SE 95th St moore</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Miya')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">James - $60 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">1016 Moraine Ave</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('James')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Aunt Tria - $40 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">409 blue spruce dr</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Aunt Tria')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Patricia Brown - $50 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">2031 Huntington Road mwc</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Patricia Brown')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Add Customer Button */}
                <div className="mt-3 ml-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-dashed border-stone-400 text-stone-600 hover:bg-stone-50"
                    onClick={() => {
                      setSelectedScheduleSection('2nd Schedule - Thursday');
                      setShowAddRecurringCustomerDialog(true);
                      setShowMowingOrderDialog(false);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to 2nd Schedule
                  </Button>
                </div>
              </div>

              {/* 3rd Schedule - Friday */}
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 text-lime-900 bg-lime-100 px-3 py-2 rounded">
                  3rd Schedule - Friday
                </h3>
                <div className="space-y-1 text-sm ml-2">
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Aunt Bobbie - $60 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">12501 Heritage oaks dr</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Aunt Bobbie')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Mrs. Anderson - $45 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">2009 Aurora Dr</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Mrs. Anderson')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Holly - $60 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">2000 rushing meadows</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Holly')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Brandon Hinton - $50 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">16212 panther way</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Brandon Hinton')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Jackie Henry - $50 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">3001 Rankin Terrace</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Jackie Henry')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Diane Jobe - $50 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">105 brad st edmond</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Diane Jobe')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Jim Thomas - $40 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">3001 Viewmont rd</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Jim Thomas')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Matthew - $80 <span className="text-xs bg-lime-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">5024 Wister Lane edmond ok</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Matthew')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Add Customer Button */}
                <div className="mt-3 ml-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-dashed border-lime-400 text-lime-700 hover:bg-lime-50"
                    onClick={() => {
                      setSelectedScheduleSection('3rd Schedule - Friday');
                      setShowAddRecurringCustomerDialog(true);
                      setShowMowingOrderDialog(false);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to 3rd Schedule
                  </Button>
                </div>
              </div>

              {/* 4th Schedule - Thursday */}
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 text-stone-900 bg-stone-100 px-3 py-2 rounded">
                  4th Schedule - Thursday
                </h3>
                <div className="space-y-1 text-sm ml-2">
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Lynne - $60 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">16740 Parkhurst road</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Lynne')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Carla Hinton - $55 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">19716 Brookville Drive</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Carla Hinton')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  
                  <div className="py-1 flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="font-medium">Glen Dennis - $60 <span className="text-xs bg-stone-100 px-2 py-1 rounded ml-2">Bi-Weekly</span></div>
                      <div className="text-xs text-stone-600">1012 Blue Ridge Dr. edmond ok</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingMowingOrder && (
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => viewCustomerFromMowingOrder('Glen Dennis')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Add Customer Button */}
                <div className="mt-3 ml-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-dashed border-stone-400 text-stone-600 hover:bg-stone-50"
                    onClick={() => {
                      setSelectedScheduleSection('4th Schedule - Thursday');
                      setShowAddRecurringCustomerDialog(true);
                      setShowMowingOrderDialog(false);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to 4th Schedule
                  </Button>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t flex items-center gap-4 text-sm text-stone-600">
                <span className="bg-stone-100 px-3 py-1.5 rounded-lg"><strong>Total:</strong> 23</span>
                <span className="bg-lime-50 text-lime-700 px-3 py-1.5 rounded-lg"><strong>Weekly:</strong> 1</span>
                <span className="bg-stone-50 text-stone-700 px-3 py-1.5 rounded-lg"><strong>Bi-Weekly:</strong> 22</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Appointment Dialog - Rendered at component level so it can be opened from any section */}
      <Dialog open={showNewAppointmentDialog} onOpenChange={setShowNewAppointmentDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Appointment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAppointment} className="space-y-4">
            <div className="relative">
              <Label>Customer Name</Label>
              <Input
                required
                data-testid="appointment-customer-name-input"
                value={newAppointment.customer_name}
                onChange={(e) => handleCustomerNameChange(e.target.value)}
                onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                placeholder="John Doe"
                autoComplete="off"
              />
              {/* Customer Suggestions Dropdown */}
              {showCustomerSuggestions && customerSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-stone-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {customerSuggestions.map((customer, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-lime-50 cursor-pointer border-b border-stone-100 last:border-b-0"
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <p className="font-semibold text-sm">{customer.name}</p>
                      <p className="text-xs text-stone-600">{customer.email}</p>
                      <p className="text-xs text-stone-500">{customer.phone}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Customer Phone (Optional)</Label>
              <Input
                data-testid="appointment-customer-phone-input"
                value={newAppointment.customer_phone}
                onChange={(e) => setNewAppointment({...newAppointment, customer_phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                required
                data-testid="appointment-customer-address-input"
                value={newAppointment.customer_address}
                onChange={(e) => setNewAppointment({...newAppointment, customer_address: e.target.value})}
                placeholder="123 Main St, OKC"
              />
            </div>
            <div>
              <Label>Customer Email (Optional)</Label>
              <Input
                type="email"
                data-testid="appointment-customer-email-input"
                value={newAppointment.customer_email}
                onChange={(e) => setNewAppointment({...newAppointment, customer_email: e.target.value})}
                onBlur={(e) => {
                  // Auto-fill from existing customer data
                  const email = e.target.value;
                  if (email) {
                    const existingCustomer = submissions.find(s => s.email === email);
                    if (existingCustomer) {
                      setNewAppointment({
                        ...newAppointment,
                        customer_name: existingCustomer.name || newAppointment.customer_name,
                        customer_phone: existingCustomer.phone || newAppointment.customer_phone,
                        customer_email: existingCustomer.email,
                        customer_address: existingCustomer.location || newAppointment.customer_address
                      });
                      toast.info('Customer details auto-filled!');
                    }
                  }
                }}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                className="w-full border rounded p-2"
                value={newAppointment.status}
                onChange={(e) => setNewAppointment({...newAppointment, status: e.target.value})}
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <Label>Services (Select all that apply)</Label>
              <div className="border rounded p-3 space-y-2">
                {['Lawn Mowing', 'Edging', 'Trimming', 'Flower Bed Maintenance', 'Small Junk Removal'].map((service) => (
                  <label key={service} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newAppointment.services.includes(service)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewAppointment({
                            ...newAppointment,
                            services: [...newAppointment.services, service]
                          });
                        } else {
                          setNewAppointment({
                            ...newAppointment,
                            services: newAppointment.services.filter(s => s !== service)
                          });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{service}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Recurring Appointment Section */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="recurring-checkbox-global"
                  checked={newAppointment.is_recurring}
                  onChange={(e) => setNewAppointment({
                    ...newAppointment, 
                    is_recurring: e.target.checked,
                    recurrence_frequency: e.target.checked ? newAppointment.recurrence_frequency : ''
                  })}
                  className="rounded"
                />
                <Label htmlFor="recurring-checkbox-global" className="cursor-pointer">
                  Recurring Appointment
                </Label>
              </div>
              
              {newAppointment.is_recurring && (
                <div>
                  <Label>How often?</Label>
                  <Input
                    value={newAppointment.recurrence_frequency}
                    onChange={(e) => setNewAppointment({...newAppointment, recurrence_frequency: e.target.value})}
                    placeholder="e.g., Weekly, Bi-weekly, Every 2 weeks, Monthly"
                  />
                </div>
              )}
            </div>
            
            <div>
              <Label>Date</Label>
              <Input
                required
                type="date"
                data-testid="appointment-date-input"
                value={newAppointment.start_time ? newAppointment.start_time.split('T')[0] : ''}
                onChange={(e) => {
                  // Set time to noon for consistency
                  const dateValue = e.target.value ? `${e.target.value}T12:00` : '';
                  setNewAppointment({
                    ...newAppointment, 
                    start_time: dateValue,
                    end_time: dateValue
                  });
                }}
              />
            </div>
            <div>
              <Label>Assign Employees (Optional)</Label>
              <div className="border rounded p-3 max-h-40 overflow-y-auto">
                {employees.map((emp) => (
                  <label key={emp.id} className="flex items-center space-x-2 mb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newAppointment.assigned_employees.includes(emp.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewAppointment({
                            ...newAppointment,
                            assigned_employees: [...newAppointment.assigned_employees, emp.id]
                          });
                        } else {
                          setNewAppointment({
                            ...newAppointment,
                            assigned_employees: newAppointment.assigned_employees.filter(id => id !== emp.id)
                          });
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{emp.name} (@{emp.username})</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                data-testid="appointment-notes-input"
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                placeholder="Special instructions..."
              />
            </div>
            
            <Button type="submit" data-testid="create-appointment-btn" className="w-full bg-green-700 hover:bg-green-800">
              Create Appointment
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
